
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { EventWithParticipants } from '@/types/social';
import { Calendar, MapPin, Users, Edit, Trash2, Award } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format, isPast } from 'date-fns';

interface EventsListProps {
  events: EventWithParticipants[];
  loading: boolean;
  onEdit: (event: EventWithParticipants) => void;
  onDelete: (eventId: string) => void;
}

const EventsList: React.FC<EventsListProps> = ({ events, loading, onEdit, onDelete }) => {
  const { t } = useTranslationUtils();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {t('admin.noEventsFound')}
      </div>
    );
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('events.name')}</TableHead>
            <TableHead>{t('events.date')}</TableHead>
            <TableHead>{t('events.location')}</TableHead>
            <TableHead className="text-center">{t('events.participants')}</TableHead>
            <TableHead className="text-right">{t('admin.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id} className={isPast(new Date(event.end_date)) ? "bg-muted/50" : ""}>
              <TableCell>
                <div className="flex items-start gap-1.5">
                  {event.festi_pass_eligible && (
                    <Award className="h-4 w-4 text-yellow-500 shrink-0 mt-1" />
                  )}
                  <div>
                    <div className="font-medium">{event.name}</div>
                    {!event.is_public && (
                      <Badge variant="outline" className="mt-1">
                        {t('admin.private')}
                      </Badge>
                    )}
                    {isPast(new Date(event.end_date)) && (
                      <Badge variant="secondary" className="mt-1 ml-1">
                        {t('admin.past')}
                      </Badge>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {format(new Date(event.start_date), 'MMM d, yyyy')}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{event.location}</span>
                </div>
              </TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-1.5">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{event.participants_count || 0}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(event)}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">{t('admin.edit')}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(event.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">{t('admin.delete')}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default EventsList;
